import React from 'react';
import { MODEL_DISPLAY_NAMES } from '../../../config/analytics';
import { formatTimeUTC6, formatDateTimeUTC6 } from '../../../utils/formatters';

interface TodayTrade {
  trade_id: number;
  instrument: string;
  direction: string;
  entry_price: number;
  exit_price: number;
  profit_loss: number;
  size: number;
  exit_reason: string;
  exit_time: string;
  ml_model_version: string;
  timeframe: string;
  entry_reason: string;
}

interface Props {
  trades: TodayTrade[];
  showDate?: boolean;  // Historical page shows MM/DD HH:MM; live monitor only HH:MM
  title?: string;      // Override the default "Today's Closed Trades" header
}

const INSTRUMENT_MODEL_MAP: Record<string, string> = {
  'NATURALGAS': 'GASBERT',
  'US100': 'USOII', 'US 100': 'USOII',
  'OIL': 'LOI', 'USOIL': 'LOI', 'CRUDE': 'LOI',
  'GOLD': 'GOI', 'XAUUSD': 'GOI', 'XAU': 'GOI',
  'SILVER': 'SILVIO', 'XAGUSD': 'SILVIO', 'XAG': 'SILVIO',
  'EURUSD': 'RUS',
  'US500': 'QUINI', 'US 500': 'QUINI',
  'GBPUSD': 'LIBRA',
  'COPPER': 'COOP',
  'USDJPY': 'YENI',
  'AUDUSD': 'DUS',
};

const resolveFromInstrument = (instrument: string): string | null => {
  if (!instrument) return null;
  const upper = instrument.toUpperCase().replace(/[^A-Z0-9]/g, '');
  for (const [key, model] of Object.entries(INSTRUMENT_MODEL_MAP)) {
    if (upper.includes(key.replace(/[^A-Z0-9]/g, ''))) return model;
  }
  return null;
};

const resolveModel = (trade: TodayTrade): string => {
  const er = trade.entry_reason || '';
  if (er.toLowerCase().startsWith('manual')) return 'MANUAL';
  const mv = trade.ml_model_version || '';
  if (MODEL_DISPLAY_NAMES[mv]) return MODEL_DISPLAY_NAMES[mv];
  if (trade.timeframe === 'HOUR_MFE_ML') return 'H1 MFE ML';
  const prefixes = ['GASBERT','USOII','LOI','GOI','SILVIO','RUS','QUINI','LIBRA','COOP','YENI','DUS'];
  for (const p of prefixes) {
    if (mv.startsWith(p)) return p;
  }
  const fromInstrument = resolveFromInstrument(trade.instrument);
  if (fromInstrument) return fromInstrument;
  return mv || trade.timeframe || '?';
};

const simplifyReason = (reason: string): string => {
  if (!reason) return '-';
  const r = reason.toUpperCase();
  if (r.includes('EMERGENCY') || r.includes('STOP-LOSS') || r.includes('STOP_LOSS')) return 'Emergency Stop';
  if (r.includes('TIER_2') || r.includes('TIER 2')) return 'Tier 2 Stop';
  if (r.includes('TIER_1') || r.includes('TIER 1')) return 'Tier 1 Stop';
  if (r.includes('TAKE_PROFIT') || r.includes('TAKE PROFIT')) return 'Take Profit';
  if (r.includes('TRAILING') || r.includes('TRAIL')) return 'Trailing Stop';
  if (r.includes('REVERSAL')) return 'Reversal';
  if (r.includes('TIMEOUT') || r.includes('TIME_LIMIT')) return 'Timeout';
  if (r.includes('MANUAL')) return 'Manual Close';
  if (r.includes('DCA') && r.includes('HARD')) return 'DCA Hard Stop';
  if (r.includes('ORPHAN')) return 'Orphan Close';
  return reason.split(':')[0].replace(/_/g, ' ').trim().substring(0, 20);
};

const TodaysTrades: React.FC<Props> = ({ trades, showDate = false, title }) => {
  const heading = title ?? "Today's Closed Trades";
  if (trades.length === 0) {
    return (
      <div className="todays-trades-section">
        <div className="todays-trades-header">
          <h3 className="todays-trades-title">{heading}</h3>
        </div>
        <p className="todays-trades-empty">No closed trades in this window</p>
      </div>
    );
  }

  const totalPL = trades.reduce((sum, t) => sum + (t.profit_loss || 0), 0);
  const wins = trades.filter(t => t.profit_loss > 0).length;
  const losses = trades.filter(t => t.profit_loss <= 0).length;

  return (
    <div className="todays-trades-section">
      <div className="todays-trades-header">
        <h3 className="todays-trades-title">{heading}</h3>
        <div className="todays-trades-summary-row">
          <span className="todays-stat-pill">{trades.length} trades</span>
          <span className="todays-stat-pill todays-wins">{wins}W</span>
          <span className="todays-stat-pill todays-losses">{losses}L</span>
          <span className={`todays-total-pl ${totalPL >= 0 ? 'pl-positive' : 'pl-negative'}`}>
            {totalPL >= 0 ? '+' : ''}${totalPL.toFixed(2)} MXN
          </span>
        </div>
      </div>
      <div className="todays-trades-table-wrap">
        <table className="todays-trades-table">
          <thead>
            <tr>
              <th></th>
              <th>Time</th>
              <th>Model</th>
              <th>Dir</th>
              <th className="hide-mobile">Entry</th>
              <th className="hide-mobile">Exit</th>
              <th className="hide-mobile">Size</th>
              <th>P/L</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {trades.map(t => {
              const isWin = t.profit_loss > 0;
              return (
                <tr key={t.trade_id} className={isWin ? 'row-win' : 'row-loss'}>
                  <td className="td-result-icon">
                    <span className={isWin ? 'result-badge result-win' : 'result-badge result-lose'}>
                      {isWin ? 'W' : 'L'}
                    </span>
                  </td>
                  <td className="td-mono">{showDate ? formatDateTimeUTC6(t.exit_time) : formatTimeUTC6(t.exit_time)}</td>
                  <td className="td-model">{resolveModel(t)}</td>
                  <td>
                    <span className={`dir-tag ${t.direction === 'BUY' ? 'dir-buy' : 'dir-sell'}`}>
                      {t.direction}
                    </span>
                  </td>
                  <td className="td-mono hide-mobile">{t.entry_price}</td>
                  <td className="td-mono hide-mobile">{t.exit_price}</td>
                  <td className="td-mono hide-mobile">{t.size}</td>
                  <td className={`td-mono td-pl ${isWin ? 'pl-positive' : 'pl-negative'}`}>
                    {isWin ? '+' : ''}${t.profit_loss?.toFixed(2)}
                  </td>
                  <td className="td-reason">{simplifyReason(t.exit_reason)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TodaysTrades;
